/**
 * Ring proposal card (the runtime HITL gate for `create_ring`). Pure
 * presentational: it shows the RESOLVED target (name + number + current
 * volume), the inner and outer offsets in CENTIMETRES (signed from the source
 * surface), the previewed ring volume, and the new structure name; nothing
 * mutates until the clinician presses Apply. The card frame, status chip, hover
 * preview, and Apply/Reject gate live in {@link ProposalCardShell}; this
 * component supplies only the body.
 */
import { formatCm3, formatCmTrimmed, formatCmWithUnit } from '~/utils/units';
import GridRoundingNote from './GridRoundingNote';
import ProposalCardShell, {
    PreviewedVolumeLine,
    ProposalSummaryBox,
    TargetLine,
    type ProposalStatus,
} from './ProposalCardShell';

export interface RingProposalCardProps {
    readonly targetName: string;
    readonly targetRoiNumber: number;
    readonly currentVolumeCm3: number;
    /** Signed cm offset from the source surface (inner boundary). */
    readonly innerCm: number;
    /** Signed cm offset from the source surface (outer; > innerCm). */
    readonly outerCm: number;
    /** Original inner offset, set only when it was rounded to the grid. */
    readonly requestedInnerCm?: number;
    /** Original outer offset, set only when it was rounded to the grid. */
    readonly requestedOuterCm?: number;
    readonly newRoi: string;
    /** Previewed volume of the ring itself (cm³). */
    readonly ringVolumeCm3: number;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function RingProposalCard({
    targetName,
    targetRoiNumber,
    currentVolumeCm3,
    innerCm,
    outerCm,
    requestedInnerCm,
    requestedOuterCm,
    newRoi,
    ringVolumeCm3,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: RingProposalCardProps) {
    const roundedParts: string[] = [];
    if (requestedInnerCm !== undefined) {
        roundedParts.push(`inner ${formatCmTrimmed(requestedInnerCm)} cm`);
    }
    if (requestedOuterCm !== undefined) {
        roundedParts.push(`outer ${formatCmTrimmed(requestedOuterCm)} cm`);
    }

    return (
        <ProposalCardShell
            title="Ring"
            ariaLabel="Ring proposal"
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <TargetLine
                targetName={targetName}
                targetRoiNumber={targetRoiNumber}
                currentVolumeCm3={currentVolumeCm3}
            />

            <ProposalSummaryBox>
                ring {formatCmWithUnit(innerCm)} → {formatCmWithUnit(outerCm)}{' '}
                on “{targetName}”{' → '}
                <strong>{newRoi}</strong>
            </ProposalSummaryBox>

            {roundedParts.length > 0 && (
                <GridRoundingNote>
                    Rounded from {roundedParts.join(', ')} to the 0.1 mm grid.
                </GridRoundingNote>
            )}

            <PreviewedVolumeLine
                label="Previewed ring volume"
                value={formatCm3(ringVolumeCm3)}
            />
        </ProposalCardShell>
    );
}
